import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Camera, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import * as exifr from 'exifr';

const SHAPE_COLORS = ['#3B82F6', '#22C55E', '#EAB308', '#F97316', '#A855F7', '#06B6D4'];
const DEDUCTION_COLOR = '#EF4444';

// Label positions stored OUTSIDE React - immune to re-renders
const labelStore = {};

// ============= COORDINATE CONVERSION UTILITIES =============
// All coordinates are stored as PERCENTAGES (0-100) relative to canvas dimensions.
// This ensures figures display correctly on any device regardless of screen size.

/**
 * Check if coordinates are percentage-based (new format) or pixel-based (legacy)
 * Percentages are always 0-100, pixels are typically > 100 for any reasonable canvas
 */
const isPercentageBased = (value) => {
  // If value is between 0 and 100, it's likely a percentage
  // Edge case: very small canvases might have pixel values < 100, but this is rare
  // For safety, we also check if the value has 'isPercent' flag (explicit marker)
  return value !== null && value !== undefined && value <= 100 && value >= 0;
};

/**
 * Convert pixel coordinates to percentage (0-100)
 */
const pixelToPercent = (pixel, dimension) => {
  if (dimension <= 0 || pixel === null || pixel === undefined) return 0;
  return (pixel / dimension) * 100;
};

/**
 * Convert percentage (0-100) to pixel coordinates
 */
const percentToPixel = (percent, dimension) => {
  if (dimension <= 0 || percent === null || percent === undefined) return 0;
  return (percent / 100) * dimension;
};

/**
 * Convert shape data from pixels to percentages for storage
 */
const shapeToPercent = (shape, canvasWidth, canvasHeight) => {
  if (!shape || canvasWidth <= 0 || canvasHeight <= 0) return shape;
  
  const converted = { ...shape, isPercent: true };
  
  if (shape.vertices) {
    converted.vertices = shape.vertices.map(v => ({
      x: pixelToPercent(v.x, canvasWidth),
      y: pixelToPercent(v.y, canvasHeight),
    }));
  }
  
  if (shape.center) {
    converted.center = {
      x: pixelToPercent(shape.center.x, canvasWidth),
      y: pixelToPercent(shape.center.y, canvasHeight),
    };
  }
  
  if (shape.radius !== undefined) {
    // Radius as percentage of the smaller dimension for consistent aspect ratio
    const minDimension = Math.min(canvasWidth, canvasHeight);
    converted.radius = pixelToPercent(shape.radius, minDimension);
  }
  
  return converted;
};

/**
 * Convert shape data from percentages to pixels for rendering
 */
const shapeToPixel = (shape, canvasWidth, canvasHeight) => {
  if (!shape || canvasWidth <= 0 || canvasHeight <= 0) return shape;
  
  // Check if already in pixel format (legacy data)
  // Legacy data will have vertex x values > 100 for any normal canvas
  const isLegacy = shape.vertices?.[0]?.x > 100 || 
                   shape.center?.x > 100 ||
                   (shape.isPercent === undefined && !isPercentageBased(shape.vertices?.[0]?.x));
  
  if (isLegacy && !shape.isPercent) {
    // Legacy pixel data - return as-is
    return shape;
  }
  
  const converted = { ...shape };
  delete converted.isPercent; // Remove marker from rendered version
  
  if (shape.vertices) {
    converted.vertices = shape.vertices.map(v => ({
      x: percentToPixel(v.x, canvasWidth),
      y: percentToPixel(v.y, canvasHeight),
    }));
  }
  
  if (shape.center) {
    converted.center = {
      x: percentToPixel(shape.center.x, canvasWidth),
      y: percentToPixel(shape.center.y, canvasHeight),
    };
  }
  
  if (shape.radius !== undefined && shape.isPercent) {
    const minDimension = Math.min(canvasWidth, canvasHeight);
    converted.radius = percentToPixel(shape.radius, minDimension);
  }
  
  return converted;
};

/**
 * Convert pins array from pixels to percentages
 */
const pinsToPercent = (pins, canvasWidth, canvasHeight) => {
  if (!pins || !Array.isArray(pins) || canvasWidth <= 0 || canvasHeight <= 0) return pins;
  
  return pins.map(pin => ({
    ...pin,
    x: pixelToPercent(pin.x, canvasWidth),
    y: pixelToPercent(pin.y, canvasHeight),
    isPercent: true,
  }));
};

/**
 * Convert pins array from percentages to pixels for rendering
 */
const pinsToPixel = (pins, canvasWidth, canvasHeight) => {
  if (!pins || !Array.isArray(pins) || canvasWidth <= 0 || canvasHeight <= 0) return pins;
  
  return pins.map(pin => {
    // Check if legacy (pixel) format
    const isLegacy = pin.x > 100 || (pin.isPercent === undefined && !isPercentageBased(pin.x));
    
    if (isLegacy && !pin.isPercent) {
      return pin; // Return as-is
    }
    
    return {
      ...pin,
      x: percentToPixel(pin.x, canvasWidth),
      y: percentToPixel(pin.y, canvasHeight),
    };
  });
};

/**
 * Convert label positions from pixels to percentages
 */
const labelsToPercent = (labels, canvasWidth, canvasHeight) => {
  if (!labels || typeof labels !== 'object' || canvasWidth <= 0 || canvasHeight <= 0) return labels;
  
  const converted = {};
  Object.keys(labels).forEach(key => {
    const label = labels[key];
    converted[key] = {
      x: pixelToPercent(label.x, canvasWidth),
      y: pixelToPercent(label.y, canvasHeight),
      rotation: label.rotation || 0,
      isPercent: true,
    };
  });
  return converted;
};

/**
 * Convert label positions from percentages to pixels for rendering
 */
const labelsToPixel = (labels, canvasWidth, canvasHeight) => {
  if (!labels || typeof labels !== 'object' || canvasWidth <= 0 || canvasHeight <= 0) return labels;
  
  const converted = {};
  Object.keys(labels).forEach(key => {
    const label = labels[key];
    // Check if legacy format
    const isLegacy = label.x > 100 || (label.isPercent === undefined && !isPercentageBased(label.x));
    
    if (isLegacy && !label.isPercent) {
      converted[key] = label; // Return as-is
    } else {
      converted[key] = {
        x: percentToPixel(label.x, canvasWidth),
        y: percentToPixel(label.y, canvasHeight),
        rotation: label.rotation || 0,
      };
    }
  });
  return converted;
};

const CAPTURE_STATES = {
  NO_PHOTO: 'NO_PHOTO',
  PHOTO_LOADED: 'PHOTO_LOADED',
  SHAPE_SELECTED: 'SHAPE_SELECTED',
  SHAPE_CONFIRMED: 'SHAPE_CONFIRMED',
  PHOTO_COMPLETE: 'PHOTO_COMPLETE',
};

export const ConstructionCanvas = forwardRef(({ 
  captureState,
  onPhotoUpload,
  onShapeCreated,
  currentShape,
  savedShapes = [],
  isDeduction = false,
  figureNumber = 1,
  selectedShapeType,
  liveMeasurements = {},
  unit = 'm²',
  pins = [],
  onPinAdd,
  // READ ONLY mode for GENERADORES
  readOnly = false,
  photoUrl = null,  // Direct photo URL for read-only mode
}, ref) => {
  const containerRef = useRef(null);
  const photoRef = useRef(null);
  const svgRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [correctedPhotoUrl, setCorrectedPhotoUrl] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, displayWidth: 0, displayHeight: 0 });
  const [draggingVertex, setDraggingVertex] = useState(null);
  const [draggingCircleCenter, setDraggingCircleCenter] = useState(false);
  const [draggingCircleEdge, setDraggingCircleEdge] = useState(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  
  // Draggable & rotatable dimension labels
  const [, forceUpdate] = useState(0); // Only to trigger re-render when dragging
  const [draggingLabel, setDraggingLabel] = useState(null);
  const [rotatingLabel, setRotatingLabel] = useState(null);
  const [rotationStart, setRotationStart] = useState(null);
  const [activeLabel, setActiveLabel] = useState(null); // Label being edited (tap to show handles)

  const isPinMode = unit === 'pza';

  useImperativeHandle(ref, () => ({
    getCanvasData: () => ({ savedShapes, currentShape, pins }),
    // Get canvas dimensions for coordinate conversion
    getCanvasDimensions: () => ({
      width: imageDimensions.displayWidth,
      height: imageDimensions.displayHeight,
    }),
    // Convert current shape to percentage coordinates for saving
    getShapeAsPercent: (shape) => {
      return shapeToPercent(shape, imageDimensions.displayWidth, imageDimensions.displayHeight);
    },
    // Convert pins to percentage coordinates for saving
    getPinsAsPercent: (pinsArray) => {
      return pinsToPercent(pinsArray, imageDimensions.displayWidth, imageDimensions.displayHeight);
    },
    // Get label positions from labelStore - converted to percentages
    getLabelPositions: () => {
      // Calculate default positions
      const rightX = Math.max(100, imageDimensions.displayWidth - 60 || 200);
      const topY = 20;
      const spacing = 30;
      
      // All possible label keys
      const labelKeys = ['largo', 'alto', 'prof', 'base', 'altura', 'baseMenor', 'baseMayor', 'diametro', 'longitud'];
      
      // Build result with current positions from labelStore or defaults
      const result = {};
      labelKeys.forEach((key, idx) => {
        if (labelStore[key]) {
          result[key] = { ...labelStore[key] };
        } else {
          result[key] = { x: rightX, y: topY + (idx * spacing), rotation: 0 };
        }
      });
      
      return result;
    },
    // Get label positions converted to percentages for saving
    getLabelPositionsAsPercent: () => {
      const labels = {};
      const rightX = Math.max(100, imageDimensions.displayWidth - 60 || 200);
      const topY = 20;
      const spacing = 30;
      const labelKeys = ['largo', 'alto', 'prof', 'base', 'altura', 'baseMenor', 'baseMayor', 'diametro', 'longitud'];
      
      labelKeys.forEach((key, idx) => {
        if (labelStore[key]) {
          labels[key] = { ...labelStore[key] };
        } else {
          labels[key] = { x: rightX, y: topY + (idx * spacing), rotation: 0 };
        }
      });
      
      return labelsToPercent(labels, imageDimensions.displayWidth, imageDimensions.displayHeight);
    },
    // Store current label positions with figureId prefix (call BEFORE clearLabelPositions)
    // This preserves positions in labelStore for the saved figure
    // positions come in PERCENTAGE format - convert to PIXELS for labelStore
    storeLabelPositionsForFigure: (figureId, positions) => {
      if (!figureId || !positions) return;
      // Convert from percentage to pixels and store with figureId prefix
      const pixelPositions = labelsToPixel(positions, imageDimensions.displayWidth, imageDimensions.displayHeight);
      Object.keys(pixelPositions).forEach(key => {
        labelStore[`${figureId}_${key}`] = { ...pixelPositions[key] };
      });
    },
    // Clear labelStore for new figure - only clear keys WITHOUT figureId prefix
    // (saved figures have keys like "shape-123_largo", current figure has keys like "largo")
    clearLabelPositions: () => {
      Object.keys(labelStore)
        .filter(key => !key.includes('_'))  // Only keys without underscore = current figure
        .forEach(key => delete labelStore[key]);
    },
    reset: () => {
      setCorrectedPhotoUrl(null);
      setImageDimensions({ width: 0, height: 0, displayWidth: 0, displayHeight: 0 });
      setShowPhotoOptions(false);
      Object.keys(labelStore).forEach(key => delete labelStore[key]);
      setActiveLabel(null);
    }
  }));

  // EXIF ORIENTATION FIX - Strip EXIF and bake rotation into pixels
  // Uses FileReader + custom image decoding to avoid ALL browser auto-rotation
  const fixOrientation = useCallback(async (file) => {
    try {
      // Step 1: Read EXIF orientation from file bytes directly
      let orientation = 1;
      try {
        const exifData = await exifr.parse(file, { pick: ['Orientation'] });
        const rawOrientation = exifData?.Orientation;
        
        if (typeof rawOrientation === 'number') {
          orientation = rawOrientation;
        } else if (typeof rawOrientation === 'string') {
          const orientationMap = {
            'Horizontal (normal)': 1,
            'Mirror horizontal': 2,
            'Rotate 180': 3,
            'Mirror vertical': 4,
            'Mirror horizontal and rotate 270 CW': 5,
            'Rotate 90 CW': 6,
            'Mirror horizontal and rotate 90 CW': 7,
            'Rotate 270 CW': 8,
          };
          orientation = orientationMap[rawOrientation] || 1;
        }
      } catch (exifError) {
        console.log('No EXIF data, using orientation 1');
      }
      
      console.log('EXIF Orientation:', orientation);
      
      // Step 2: Load image - browser WILL auto-rotate based on EXIF
      // We need to account for this and NOT apply rotation ourselves
      return new Promise((resolve) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const img = new Image();
          
          img.onload = () => {
            const loadedW = img.naturalWidth;
            const loadedH = img.naturalHeight;
            
            console.log('Image loaded:', loadedW, 'x', loadedH);
            
            // Modern browsers auto-rotate based on EXIF
            // The loaded image is ALREADY in the correct visual orientation
            // So we just draw it as-is to a canvas to strip EXIF metadata
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            canvas.width = loadedW;
            canvas.height = loadedH;
            ctx.drawImage(img, 0, 0);
            
            // Export strips EXIF, image is now orientation-correct in pixels
            const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            
            console.log('Output:', canvas.width, 'x', canvas.height);
            
            resolve({ 
              dataUrl, 
              width: canvas.width, 
              height: canvas.height 
            });
          };
          
          img.onerror = () => {
            console.error('Image load error');
            resolve({ dataUrl: null, width: 0, height: 0 });
          };
          
          img.src = e.target.result;
        };
        
        reader.onerror = () => {
          console.error('FileReader error');
          resolve({ dataUrl: null, width: 0, height: 0 });
        };
        
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('fixOrientation error:', error);
      return { dataUrl: null, width: 0, height: 0 };
    }
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const { dataUrl, width, height } = await fixOrientation(file);
      if (dataUrl) {
        setCorrectedPhotoUrl(dataUrl);
        setImageDimensions(prev => ({ ...prev, width, height }));
        setShowPhotoOptions(false);
        if (onPhotoUpload) onPhotoUpload(dataUrl);
      }
    } catch (error) {
      console.error('Error processing photo:', error);
      toast.error('Error al procesar la foto');
    }
  };

  const handleImageLoad = useCallback(() => {
    if (!photoRef.current) return;
    const img = photoRef.current;
    const rect = img.getBoundingClientRect();
    setImageDimensions(prev => ({ ...prev, displayWidth: rect.width, displayHeight: rect.height }));
  }, []);

  useEffect(() => {
    if (!correctedPhotoUrl && !photoUrl) return;
    window.addEventListener('resize', handleImageLoad);
    return () => window.removeEventListener('resize', handleImageLoad);
  }, [correctedPhotoUrl, photoUrl, handleImageLoad]);

  // In readOnly mode, use photoUrl directly
  useEffect(() => {
    if (readOnly && photoUrl) {
      setCorrectedPhotoUrl(photoUrl);
    }
  }, [readOnly, photoUrl]);

  // Create shape when type is selected
  useEffect(() => {
    if (captureState !== CAPTURE_STATES.SHAPE_SELECTED || !selectedShapeType) return;
    if (currentShape) return;
    if (!imageDimensions.displayWidth || !imageDimensions.displayHeight) return;
    if (isPinMode) return;

    const { displayWidth, displayHeight } = imageDimensions;
    const centerX = displayWidth / 2;
    const centerY = displayHeight / 2;
    const size = Math.min(displayWidth, displayHeight) * 0.25;

    const colorIndex = savedShapes.length % SHAPE_COLORS.length;
    // Use figureNumber for color rotation (consistent across sessions)
    // figureNumber is 1-based, so subtract 1 for 0-based index
    const globalColorIndex = (figureNumber - 1) % SHAPE_COLORS.length;
    const color = isDeduction ? DEDUCTION_COLOR : SHAPE_COLORS[globalColorIndex];
    
    // Log color assignment for debugging
    console.log(`FIGURE COLOR: figureNumber=${figureNumber}, isDeduction=${isDeduction}, color=${color}`);

    let newShape = null;

    switch (selectedShapeType) {
      case 'rectangle':
        newShape = {
          id: `shape-${Date.now()}`,
          type: 'rectangle',
          figureNumber,
          vertices: [
            { x: centerX - size / 2, y: centerY - size * 0.35 },
            { x: centerX + size / 2, y: centerY - size * 0.35 },
            { x: centerX + size / 2, y: centerY + size * 0.35 },
            { x: centerX - size / 2, y: centerY + size * 0.35 },
          ],
          color,
          isDeduction,
        };
        break;
      case 'triangle':
        newShape = {
          id: `shape-${Date.now()}`,
          type: 'triangle',
          figureNumber,
          vertices: [
            { x: centerX, y: centerY - size / 2 },
            { x: centerX + size / 2, y: centerY + size / 2 },
            { x: centerX - size / 2, y: centerY + size / 2 },
          ],
          color,
          isDeduction,
        };
        break;
      case 'trapezoid':
        newShape = {
          id: `shape-${Date.now()}`,
          type: 'trapezoid',
          figureNumber,
          vertices: [
            { x: centerX - size * 0.3, y: centerY - size / 2 },
            { x: centerX + size * 0.3, y: centerY - size / 2 },
            { x: centerX + size / 2, y: centerY + size / 2 },
            { x: centerX - size / 2, y: centerY + size / 2 },
          ],
          color,
          isDeduction,
        };
        break;
      case 'circle':
        newShape = {
          id: `shape-${Date.now()}`,
          type: 'circle',
          figureNumber,
          center: { x: centerX, y: centerY },
          radius: size / 2,
          color,
          isDeduction,
        };
        break;
      case 'line':
        newShape = {
          id: `shape-${Date.now()}`,
          type: 'line',
          figureNumber,
          vertices: [
            { x: centerX - size, y: centerY },
            { x: centerX + size, y: centerY },
          ],
          color,
          isDeduction,
        };
        break;
      default:
        return;
    }

    onShapeCreated(newShape);
    toast.info('Arrastra los puntos para ajustar');
  }, [captureState, selectedShapeType, savedShapes.length, isDeduction, figureNumber, onShapeCreated, currentShape, imageDimensions, isPinMode]);

  // Handle canvas click for pin mode - only ADD pins, don't remove on click
  // Also deactivate active label when clicking elsewhere
  const handleCanvasClick = useCallback((e) => {
    // Deactivate any active label when clicking on canvas
    if (activeLabel) {
      setActiveLabel(null);
    }
    
    if (!isPinMode || !onPinAdd) return;
    if (captureState !== CAPTURE_STATES.PHOTO_LOADED) return;
    
    const svg = svgRef.current;
    if (!svg) return;
    
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Add new pin at click position
    onPinAdd({ x, y, number: pins.length + 1 });
    toast.success(`Pin ${pins.length + 1} agregado`);
  }, [isPinMode, onPinAdd, captureState, pins.length, activeLabel]);

  // Vertex dragging
  const handleVertexMouseDown = (vertexIndex, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (captureState !== CAPTURE_STATES.SHAPE_SELECTED) return;
    setDraggingVertex(vertexIndex);
  };

  const handleCircleCenterMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (captureState !== CAPTURE_STATES.SHAPE_SELECTED) return;
    setDraggingCircleCenter(true);
  };

  const handleCircleEdgeMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (captureState !== CAPTURE_STATES.SHAPE_SELECTED) return;
    setDraggingCircleEdge(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (captureState !== CAPTURE_STATES.SHAPE_SELECTED || !currentShape) return;
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));

    if (draggingVertex !== null && currentShape.vertices) {
      const newVertices = [...currentShape.vertices];
      newVertices[draggingVertex] = { x, y };
      onShapeCreated({ ...currentShape, vertices: newVertices });
    } else if (draggingCircleCenter && currentShape.type === 'circle') {
      onShapeCreated({ ...currentShape, center: { x, y } });
    } else if (draggingCircleEdge && currentShape.type === 'circle') {
      const dx = x - currentShape.center.x;
      const dy = y - currentShape.center.y;
      const newRadius = Math.max(20, Math.sqrt(dx * dx + dy * dy));
      onShapeCreated({ ...currentShape, radius: newRadius });
    }
  }, [draggingVertex, draggingCircleCenter, draggingCircleEdge, currentShape, captureState, onShapeCreated]);

  const handleTouchMove = useCallback((e) => {
    if (captureState !== CAPTURE_STATES.SHAPE_SELECTED || !currentShape) return;
    if (!svgRef.current) return;

    e.preventDefault();
    const touch = e.touches[0];
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, touch.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, touch.clientY - rect.top));

    if (draggingVertex !== null && currentShape.vertices) {
      const newVertices = [...currentShape.vertices];
      newVertices[draggingVertex] = { x, y };
      onShapeCreated({ ...currentShape, vertices: newVertices });
    } else if (draggingCircleCenter && currentShape.type === 'circle') {
      onShapeCreated({ ...currentShape, center: { x, y } });
    } else if (draggingCircleEdge && currentShape.type === 'circle') {
      const dx = x - currentShape.center.x;
      const dy = y - currentShape.center.y;
      const newRadius = Math.max(20, Math.sqrt(dx * dx + dy * dy));
      onShapeCreated({ ...currentShape, radius: newRadius });
    }
  }, [draggingVertex, draggingCircleCenter, draggingCircleEdge, currentShape, captureState, onShapeCreated]);

  const handleMouseUp = useCallback(() => {
    setDraggingVertex(null);
    setDraggingCircleCenter(false);
    setDraggingCircleEdge(false);
  }, []);

  // SVG path helpers
  const verticesToPath = (vertices) => {
    if (!vertices || vertices.length === 0) return '';
    if (vertices.length === 2) {
      return `M ${vertices[0].x.toFixed(1)} ${vertices[0].y.toFixed(1)} L ${vertices[1].x.toFixed(1)} ${vertices[1].y.toFixed(1)}`;
    }
    return vertices.map((v, i) => `${i === 0 ? 'M' : 'L'} ${v.x.toFixed(1)} ${v.y.toFixed(1)}`).join(' ') + ' Z';
  };

  const getShapeCenter = (shape) => {
    if (shape.type === 'circle') return shape.center;
    const xs = shape.vertices.map(v => v.x);
    const ys = shape.vertices.map(v => v.y);
    return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
  };

  const getShapeBounds = (shape) => {
    if (shape.type === 'circle') {
      return {
        minX: shape.center.x - shape.radius,
        maxX: shape.center.x + shape.radius,
        minY: shape.center.y - shape.radius,
        maxY: shape.center.y + shape.radius,
      };
    }
    const xs = shape.vertices.map(v => v.x);
    const ys = shape.vertices.map(v => v.y);
    return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
  };

  // Render figure number INSIDE the shape (centered)
  const renderFigureLabel = (shape) => {
    const center = getShapeCenter(shape);
    return (
      <g>
        <rect x={center.x - 32} y={center.y - 9} width={64} height={18} fill={shape.color} rx="4" opacity="0.95" />
        <text x={center.x} y={center.y + 4} textAnchor="middle" fill="white" fontSize="11" fontWeight="bold" fontFamily="monospace">
          {String(shape.figureNumber).padStart(6, '0')}
        </text>
      </g>
    );
  };

  // Get label position from labelStore or return default
  const getLabelPosition = (labelKey, defaultX, defaultY) => {
    if (labelStore[labelKey]) {
      return labelStore[labelKey];
    }
    return { x: defaultX, y: defaultY, rotation: 0 };
  };

  // Handle label drag start
  const handleLabelDragStart = (labelKey) => (e) => {
    e.stopPropagation();
    setDraggingLabel(labelKey);
  };

  // Handle rotation start
  const handleRotationStart = (labelKey) => (e) => {
    e.stopPropagation();
    setRotatingLabel(labelKey);
    const svg = svgRef.current;
    if (svg) {
      const rect = svg.getBoundingClientRect();
      const pos = labelStore[labelKey] || { x: 0, y: 0, rotation: 0 };
      const centerX = pos.x;
      const centerY = pos.y;
      
      // Handle both mouse and touch events
      let clientX, clientY;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      
      const mouseX = clientX - rect.left;
      const mouseY = clientY - rect.top;
      const startAngle = Math.atan2(mouseY - centerY, mouseX - centerX);
      setRotationStart({ angle: startAngle, initialRotation: pos.rotation || 0 });
    }
  };

  // Handle mouse move for labels (drag and rotate)
  useEffect(() => {
    const handleMove = (e) => {
      const svg = svgRef.current;
      if (!svg) return;
      
      const rect = svg.getBoundingClientRect();
      
      // Handle both mouse and touch events
      let clientX, clientY;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      
      // Calculate position relative to SVG canvas
      const mouseX = clientX - rect.left;
      const mouseY = clientY - rect.top;
      
      // Dragging label - update labelStore directly (outside React)
      if (draggingLabel) {
        const x = Math.max(40, Math.min(mouseX, imageDimensions.displayWidth - 40));
        const y = Math.max(20, Math.min(mouseY, imageDimensions.displayHeight - 20));
        const currentPos = labelStore[draggingLabel] || { rotation: 0 };
        labelStore[draggingLabel] = { x, y, rotation: currentPos.rotation || 0 };
        forceUpdate(n => n + 1); // Trigger re-render to show new position
      }
      
      // Rotating label - update labelStore directly
      if (rotatingLabel && rotationStart) {
        const pos = labelStore[rotatingLabel] || { x: 0, y: 0, rotation: 0 };
        const currentAngle = Math.atan2(mouseY - pos.y, mouseX - pos.x);
        const deltaAngle = (currentAngle - rotationStart.angle) * (180 / Math.PI);
        const newRotation = rotationStart.initialRotation + deltaAngle;
        labelStore[rotatingLabel] = { ...pos, rotation: newRotation };
        forceUpdate(n => n + 1); // Trigger re-render to show new rotation
      }
    };

    const handleEnd = () => {
      setDraggingLabel(null);
      setRotatingLabel(null);
      setRotationStart(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [draggingLabel, rotatingLabel, rotationStart, imageDimensions]);

  // Label prefixes for each dimension type
  const LABEL_PREFIXES = {
    largo: 'L',
    alto: 'A',
    profundidad: 'Prof',
    prof: 'Prof',
    diametro: 'D',
    base: 'B',
    baseMenor: 'Bm',
    baseMayor: 'BM',
    altura: 'h',
    longitud: 'L',
  };

  // Handle label tap - show handles for that label only
  const handleLabelTap = (key) => (e) => {
    e.stopPropagation();
    setActiveLabel(key);
  };

  // Render a single draggable & rotatable text label
  // isLocked: for saved figures, handles only show when label is tapped (activeLabel)
  // For current shape (not saved), handles always visible
  // In readOnly mode, never show handles
  const renderDraggableLabel = (key, value, posX, posY, color, isLocked = false, savedRotation = 0) => {
    // Get position from ref (or initialize with default)
    const pos = getLabelPosition(key, posX, posY);
    
    // Use saved rotation if no rotation in ref
    const rotation = pos.rotation || savedRotation || 0;
    
    // Get the base key without figureId prefix for prefix lookup
    const baseKey = key.includes('_') ? key.split('_').pop() : key;
    
    // Add prefix based on dimension type
    const prefix = LABEL_PREFIXES[baseKey] || '';
    const displayValue = value ? `${prefix}: ${parseFloat(value).toFixed(2)}m` : '?';
    
    // Calculate width based on text length
    const labelWidth = displayValue.length * 8 + 16;
    
    // Show handles: never in readOnly, always for current shape, only when tapped for saved shapes
    const showHandles = !readOnly && (!isLocked || activeLabel === key);
    
    return (
      <g 
        key={key}
        transform={`rotate(${rotation}, ${pos.x}, ${pos.y})`}
      >
        {/* Label background - tap to activate editing (not in readOnly) */}
        <rect 
          x={pos.x - labelWidth/2} 
          y={pos.y - 10} 
          width={labelWidth} 
          height={20} 
          fill="white" 
          stroke={color} 
          strokeWidth="2"
          rx="4"
          style={{ cursor: readOnly ? 'default' : (showHandles ? 'move' : 'pointer') }}
          onClick={readOnly ? undefined : (isLocked ? handleLabelTap(key) : undefined)}
          onMouseDown={readOnly ? undefined : (showHandles ? handleLabelDragStart(key) : undefined)}
          onTouchStart={readOnly ? undefined : (showHandles 
            ? (e) => { e.stopPropagation(); setDraggingLabel(key); }
            : (e) => { e.stopPropagation(); setActiveLabel(key); }
          )}
        />
        {/* Label text */}
        <text 
          x={pos.x} 
          y={pos.y + 4} 
          textAnchor="middle" 
          fill={color} 
          fontSize="12" 
          fontWeight="bold" 
          fontFamily="monospace"
          style={{ pointerEvents: 'none' }}
        >
          {displayValue}
        </text>
        
        {/* Drag handle - visible when active or not locked */}
        {showHandles && (
          <rect 
            x={pos.x - 6} 
            y={pos.y - 28} 
            width={12} 
            height={12} 
            fill={color} 
            stroke="white" 
            strokeWidth="2"
            rx="2"
            style={{ cursor: 'move' }}
            onMouseDown={handleLabelDragStart(key)}
            onTouchStart={(e) => { e.stopPropagation(); setDraggingLabel(key); }}
          />
        )}
        
        {/* Rotation handle - visible when active or not locked */}
        {showHandles && (
          <circle
            cx={pos.x + labelWidth/2 + 10}
            cy={pos.y}
            r="8"
            fill="white"
            stroke={color}
            strokeWidth="2"
            style={{ cursor: 'crosshair' }}
            onMouseDown={handleRotationStart(key)}
            onTouchStart={(e) => { e.stopPropagation(); handleRotationStart(key)(e); }}
          />
        )}
      </g>
    );
  };

  // Render DRAGGABLE & ROTATABLE dimension labels
  // Shows real values from liveMeasurements (form fields)
  // Labels are ALWAYS editable for position/rotation
  // savedLabelPositions = positions saved with the figure (may be in percentage format)
  // figureId = unique identifier to scope label states per figure (prevents color bleeding)
  const renderDimensionLabels = (shape, measurements, isLocked = false, savedLabelPositions = null, figureId = null) => {
    if (!measurements) return null;
    const { largo, alto, profundidad, base, altura, baseMayor, baseMenor, diametro, longitud, shapeType, unit: measureUnit } = measurements;
    
    // Determine if this is a m³ measurement - either by unit or by having profundidad
    const isVolumetric = measureUnit === 'm³' || (profundidad && parseFloat(profundidad) > 0);
    
    // Use shape's color (locked at save time) - this prevents color changing when mode switches
    const shapeColor = shape.color || '#3B82F6';
    
    // Fixed initial positions at top-right corner of canvas, stacked vertically
    const rightX = Math.max(100, imageDimensions.displayWidth - 60);
    const topY = 20;
    const spacing = 30;
    
    // Convert savedLabelPositions from percentage to pixel if needed
    const pixelLabelPositions = savedLabelPositions 
      ? labelsToPixel(savedLabelPositions, imageDimensions.displayWidth, imageDimensions.displayHeight)
      : null;
    
    const labels = [];
    let labelIndex = 0;
    
    // Helper to render label
    const renderLabel = (baseKey, value) => {
      const defaultX = rightX;
      const defaultY = topY + (labelIndex * spacing);
      
      let posX, posY, rotation;
      
      // For saved figures: try savedLabelPositions first, then labelStore with figureId prefix
      const figureKey = figureId ? `${figureId}_${baseKey}` : baseKey;
      
      if (isLocked && pixelLabelPositions?.[baseKey]) {
        // Saved figure: use stored positions from shape object (converted from percentage)
        posX = pixelLabelPositions[baseKey].x;
        posY = pixelLabelPositions[baseKey].y;
        rotation = pixelLabelPositions[baseKey].rotation || 0;
      } else if (isLocked && labelStore[figureKey]) {
        // Saved figure: fallback to labelStore with figureId prefix
        posX = labelStore[figureKey].x;
        posY = labelStore[figureKey].y;
        rotation = labelStore[figureKey].rotation || 0;
      } else {
        // Current figure: use labelStore positions (outside React)
        const pos = getLabelPosition(baseKey, defaultX, defaultY);
        posX = pos.x;
        posY = pos.y;
        rotation = pos.rotation || 0;
      }
      
      labelIndex++;
      
      // Use figureId prefix for keys to make them unique per figure
      const uniqueKey = figureId ? `${figureId}_${baseKey}` : baseKey;
      
      return renderDraggableLabel(uniqueKey, value, posX, posY, shapeColor, isLocked, rotation);
    };
    
    // LINE (ml) - Single label
    if (shape.type === 'line') {
      labels.push(renderLabel('longitud', longitud));
      return <g className="dimension-labels">{labels}</g>;
    }
    
    // CIRCLE - Diameter label (+ P if volumetric)
    if (shape.type === 'circle') {
      labels.push(renderLabel('diametro', diametro));
      if (isVolumetric) {
        labels.push(renderLabel('prof', profundidad));
      }
      return <g className="dimension-labels">{labels}</g>;
    }

    // RECTANGLE - L, A (+ P if volumetric)
    if (shapeType === 'rectangle' || shape.type === 'rectangle') {
      labels.push(renderLabel('largo', largo));
      labels.push(renderLabel('alto', alto));
      if (isVolumetric) {
        labels.push(renderLabel('prof', profundidad));
      }
    }

    // TRIANGLE - B, h (+ P if volumetric)
    if (shapeType === 'triangle' || shape.type === 'triangle') {
      labels.push(renderLabel('base', base));
      labels.push(renderLabel('altura', altura));
      if (isVolumetric) {
        labels.push(renderLabel('prof', profundidad));
      }
    }

    // TRAPEZOID - Bm, BM, h (+ P if volumetric)
    if (shapeType === 'trapezoid' || shape.type === 'trapezoid') {
      labels.push(renderLabel('baseMenor', baseMenor));
      labels.push(renderLabel('baseMayor', baseMayor));
      labels.push(renderLabel('altura', altura));
      if (isVolumetric) {
        labels.push(renderLabel('prof', profundidad));
      }
    }

    return <g className="dimension-labels">{labels}</g>;
  };

  // Render pins for pza mode - each pin has its own color (stored when placed)
  // Converts from percentage to pixel coordinates for display
  const renderPins = () => {
    // Convert pins from percentage to pixel coordinates
    const pixelPins = pinsToPixel(pins, imageDimensions.displayWidth, imageDimensions.displayHeight);
    
    return pixelPins.map((pin, idx) => {
      // Use pin's stored color (green for SUMA, red for DEDUCCIÓN at time of placement)
      const pinColor = pin.color || (isDeduction ? '#EF4444' : '#22C55E');
      
      return (
        <g key={idx} style={{ cursor: 'pointer' }} data-testid={`pin-${pin.number}`}>
          {/* Transparent circle with colored border */}
          <circle 
            cx={pin.x} 
            cy={pin.y} 
            r="18" 
            fill="rgba(255,255,255,0.4)" 
            stroke={pinColor} 
            strokeWidth="3" 
          />
          {/* Number */}
          <text 
            x={pin.x} 
            y={pin.y + 5} 
            textAnchor="middle" 
            fill={pinColor} 
            fontSize="14" 
            fontWeight="bold" 
            fontFamily="monospace"
            style={{ textShadow: '0 0 3px white, 0 0 3px white' }}
          >
            {pin.number}
          </text>
        </g>
      );
    });
  };

  // NO_PHOTO state - Skip in readOnly mode (loading handled by photoUrl)
  if (!readOnly && (captureState === CAPTURE_STATES.NO_PHOTO || !correctedPhotoUrl)) {
    return (
      <div ref={containerRef} className="canvas-container no-photo" data-testid="canvas-no-photo">
        {!showPhotoOptions ? (
          <Button
            onClick={() => setShowPhotoOptions(true)}
            className="h-16 px-10 bg-[#1B3A6B] hover:bg-[#142B50] text-white text-lg font-semibold"
            data-testid="load-photo-btn"
          >
            <Camera className="w-6 h-6 mr-3" />
            CARGAR FOTO
          </Button>
        ) : (
          <div className="flex flex-col gap-4">
            <Button onClick={() => cameraInputRef.current?.click()} className="h-14 px-8 bg-[#1B3A6B] hover:bg-[#142B50] text-white font-semibold" data-testid="take-photo-btn">
              <Camera className="w-5 h-5 mr-2" />
              Tomar foto
            </Button>
            <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="h-14 px-8 font-semibold bg-white" data-testid="select-photo-btn">
              <ImageIcon className="w-5 h-5 mr-2" />
              Elegir de fototeca
            </Button>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" />
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
          </div>
        )}
      </div>
    );
  }

  // ReadOnly mode loading state
  if (readOnly && !correctedPhotoUrl) {
    return (
      <div ref={containerRef} className="canvas-container no-photo" data-testid="canvas-loading">
        <div className="text-slate-400">Cargando foto...</div>
      </div>
    );
  }

  // WITH PHOTO
  return (
    <div ref={containerRef} className="canvas-container with-photo" data-testid="canvas-with-photo">
      <div className="photo-wrapper">
        <img
          ref={photoRef}
          src={correctedPhotoUrl}
          alt="Foto de construcción"
          onLoad={handleImageLoad}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
          data-testid="construction-photo"
        />

        {imageDimensions.displayWidth > 0 && (
          <svg
            ref={svgRef}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: imageDimensions.displayWidth,
              height: imageDimensions.displayHeight,
              cursor: readOnly ? 'default' : (isPinMode ? 'crosshair' : (draggingVertex !== null || draggingCircleCenter || draggingCircleEdge) ? 'grabbing' : 'default'),
              touchAction: readOnly ? 'auto' : 'none',
              pointerEvents: readOnly ? 'none' : 'auto',
            }}
            onClick={readOnly ? undefined : handleCanvasClick}
            onMouseMove={readOnly ? undefined : handleMouseMove}
            onMouseUp={readOnly ? undefined : handleMouseUp}
            onMouseLeave={readOnly ? undefined : handleMouseUp}
            onTouchMove={readOnly ? undefined : handleTouchMove}
            onTouchEnd={readOnly ? undefined : handleMouseUp}
            data-testid="svg-overlay"
          >
            {/* Saved shapes - figures only - Convert from percentage to pixels */}
            {savedShapes.map((shape) => {
              // Convert shape from percentage to pixel coordinates
              const pixelShape = shapeToPixel(shape, imageDimensions.displayWidth, imageDimensions.displayHeight);
              return (
                <g key={shape.id}>
                  {pixelShape.type === 'circle' ? (
                    <circle cx={pixelShape.center.x} cy={pixelShape.center.y} r={pixelShape.radius} fill={`${pixelShape.color}30`} stroke={pixelShape.color} strokeWidth="3" />
                  ) : pixelShape.type === 'line' ? (
                    <path d={verticesToPath(pixelShape.vertices)} fill="none" stroke={pixelShape.color} strokeWidth="4" />
                  ) : (
                    <path d={verticesToPath(pixelShape.vertices)} fill={`${pixelShape.color}30`} stroke={pixelShape.color} strokeWidth="3" />
                  )}
                  {renderFigureLabel(pixelShape)}
                </g>
              );
            })}

            {/* Current shape */}
            {currentShape && (
              <g data-testid="current-shape">
                {currentShape.type === 'circle' ? (
                  <>
                    <circle cx={currentShape.center.x} cy={currentShape.center.y} r={currentShape.radius} fill={`${currentShape.color}25`} stroke={currentShape.color} strokeWidth="3" strokeDasharray={captureState === CAPTURE_STATES.SHAPE_SELECTED ? '8,4' : '0'} />
                    {captureState === CAPTURE_STATES.SHAPE_SELECTED && (
                      <>
                        {/* Drag handle - small square on top */}
                        <rect 
                          x={currentShape.center.x - 10} 
                          y={currentShape.center.y - currentShape.radius - 22} 
                          width="20" 
                          height="20" 
                          fill={currentShape.color} 
                          stroke="white" 
                          strokeWidth="3" 
                          rx="3"
                          style={{ cursor: 'move' }} 
                          onMouseDown={handleCircleCenterMouseDown} 
                          onTouchStart={(e) => { e.stopPropagation(); setDraggingCircleCenter(true); }} 
                        />
                        {/* Center circle for visual reference */}
                        <circle cx={currentShape.center.x} cy={currentShape.center.y} r="8" fill={currentShape.color} stroke="white" strokeWidth="2" style={{ cursor: 'move' }} onMouseDown={handleCircleCenterMouseDown} onTouchStart={(e) => { e.stopPropagation(); setDraggingCircleCenter(true); }} />
                        {/* Resize handle on right edge */}
                        <circle cx={currentShape.center.x + currentShape.radius} cy={currentShape.center.y} r="14" fill="white" stroke={currentShape.color} strokeWidth="3" style={{ cursor: 'ew-resize' }} onMouseDown={handleCircleEdgeMouseDown} onTouchStart={(e) => { e.stopPropagation(); setDraggingCircleEdge(true); }} />
                      </>
                    )}
                  </>
                ) : currentShape.type === 'line' ? (
                  <>
                    <path d={verticesToPath(currentShape.vertices)} fill="none" stroke={currentShape.color} strokeWidth="4" strokeDasharray={captureState === CAPTURE_STATES.SHAPE_SELECTED ? '8,4' : '0'} />
                    {captureState === CAPTURE_STATES.SHAPE_SELECTED && currentShape.vertices.map((vertex, index) => (
                      <circle key={index} cx={vertex.x} cy={vertex.y} r="14" fill={currentShape.color} stroke="white" strokeWidth="3" style={{ cursor: 'grab' }} onMouseDown={(e) => handleVertexMouseDown(index, e)} onTouchStart={(e) => { e.stopPropagation(); setDraggingVertex(index); }} />
                    ))}
                  </>
                ) : (
                  <>
                    <path d={verticesToPath(currentShape.vertices)} fill={`${currentShape.color}25`} stroke={currentShape.color} strokeWidth="3" strokeDasharray={captureState === CAPTURE_STATES.SHAPE_SELECTED ? '8,4' : '0'} />
                    {captureState === CAPTURE_STATES.SHAPE_SELECTED && currentShape.vertices.map((vertex, index) => (
                      <circle key={index} cx={vertex.x} cy={vertex.y} r="14" fill={currentShape.color} stroke="white" strokeWidth="3" style={{ cursor: 'grab' }} onMouseDown={(e) => handleVertexMouseDown(index, e)} onTouchStart={(e) => { e.stopPropagation(); setDraggingVertex(index); }} />
                    ))}
                  </>
                )}
                {renderFigureLabel(currentShape)}
              </g>
            )}

            {/* Pins for pza mode */}
            {isPinMode && renderPins()}

            {/* ALL DIMENSION LABELS - Rendered last to be on top layer */}
            <g className="dimension-labels-layer" style={{ pointerEvents: 'all' }}>
              {/* Saved shapes dimension labels - each with unique figureId */}
              {savedShapes.map((shape) => {
                // Convert shape from percentage to pixel coordinates for rendering
                const pixelShape = shapeToPixel(shape, imageDimensions.displayWidth, imageDimensions.displayHeight);
                return (
                  <g key={`labels-${shape.id}`}>
                    {shape.measurements && renderDimensionLabels(pixelShape, shape.measurements, true, shape.labelPositions, shape.id)}
                  </g>
                );
              })}
              {/* Current shape dimension labels - use null figureId so keys are simple like 'largo' */}
              {currentShape && captureState === CAPTURE_STATES.SHAPE_CONFIRMED && renderDimensionLabels(currentShape, liveMeasurements, false, null, null)}
            </g>
          </svg>
        )}
      </div>
    </div>
  );
});

ConstructionCanvas.displayName = 'ConstructionCanvas';
export default ConstructionCanvas;
